import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert, Spinner, Row, Col, Nav, Tab } from 'react-bootstrap';
import { FiArrowLeft, FiUpload, FiDownload, FiInfo } from 'react-icons/fi';
import {
    importFlowerTripModified,
    importLacdesgravesTripModified,
    importOlelaTripModified,
    importAloaTripModified,
    importOdalysTripModified,
    importTripsTripModifiedGeneric,
} from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import { downloadModifiedPoliciesStandardTemplate } from '../../utils/standardImportTemplate';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsPolicies.css';

const PAGE_PREFIX = 'pages.adminTripsPoliciesModifiedImport';

const IMPORT_MODES = {
    STANDARD: 'standard',
    SPECIFIC: 'specific',
};

const SPECIFIC_PARTNER_OPTIONS = ['flower', 'odalys', 'aloa', 'lacdesgraves', 'olela'];

const IMPORT_FNS = {
    standard: importTripsTripModifiedGeneric,
    flower: importFlowerTripModified,
    olela: importOlelaTripModified,
    lacdesgraves: importLacdesgravesTripModified,
    aloa: importAloaTripModified,
    odalys: importOdalysTripModified,
};

const PARTNER_LABELS = {
    standard: 'Standard',
    flower: 'Flower',
    olela: 'Olela',
    lacdesgraves: 'Lac des Graves',
    aloa: 'Aloa Vacances',
    odalys: 'Odalys Vacances',
};

const TEMPLATE_URLS = {
    flower: 'https://eu-west-3.console.aws.amazon.com/s3/object/admin-template-dashboard?region=eu-west-3&prefix=flower/flower_template_trip_modified.xlsx',
    olela: 'https://eu-west-3.console.aws.amazon.com/s3/object/admin-template-dashboard?region=eu-west-3&prefix=ol%C3%A9la/olela_template_modified.xlsx',
    lacdesgraves: 'https://eu-west-3.console.aws.amazon.com/s3/object/admin-template-dashboard?region=eu-west-3&prefix=lacdesgraves/lac_des_graves_templace_update_trips.xlsx',
};

const buildTripModifiedSuccessMessage = (response, partnerName) => {
    let successMessage = `Import des modifications de séjours ${partnerName} terminé.\n\n`;

    if (response?.successful_updates !== undefined) {
        successMessage += `✅ Séjours mis à jour avec succès : ${response.successful_updates}\n`;
    } else if (response?.success_count !== undefined) {
        successMessage += `✅ Séjours mis à jour avec succès : ${response.success_count}\n`;
    }

    if (response?.failed_updates !== undefined) {
        successMessage += `❌ Échecs : ${response.failed_updates}\n`;
    } else if (response?.error_count !== undefined) {
        successMessage += `❌ Erreurs : ${response.error_count}\n`;
    }

    if (response?.results?.successes && Array.isArray(response.results.successes) && response.results.successes.length > 0) {
        successMessage += `\n📋 Séjours mis à jour avec succès :\n`;
        response.results.successes.forEach((success, index) => {
            const reservationNumber = success.reservation_number || success.reservationNumber || 'N/A';
            successMessage += `${index + 1}. Réservation ${reservationNumber}\n`;
        });
    }

    if (response?.results?.errors && Array.isArray(response.results.errors) && response.results.errors.length > 0) {
        successMessage += `\n⚠️ Détails des erreurs :\n`;
        response.results.errors.forEach((error, index) => {
            const reservationNumber = error.reservation_number || error.reservationNumber || 'N/A';
            const errorMsg = error.error || error.message || 'Erreur inconnue';
            successMessage += `${index + 1}. Réservation ${reservationNumber}: ${errorMsg || 'Erreur non spécifiée'}\n`;
        });
    }

    if (response?.updated_trips && Array.isArray(response.updated_trips) && response.updated_trips.length > 0) {
        successMessage += `\n📋 Séjours mis à jour : ${response.updated_trips.join(', ')}\n`;
    }
    if (response?.updated_policies && Array.isArray(response.updated_policies) && response.updated_policies.length > 0) {
        successMessage += `\n📋 Polices mises à jour : ${response.updated_policies.join(', ')}\n`;
    }
    if (response?.errors && Array.isArray(response.errors) && response.errors.length > 0 && !response.results?.errors) {
        successMessage += `\n⚠️ Détails des erreurs :\n`;
        response.errors.forEach((error, index) => {
            const reservationNumber = error.reservation_number || error.reservationNumber || 'N/A';
            const errorMsg = error.error || error.message || 'Erreur inconnue';
            successMessage += `${index + 1}. Réservation ${reservationNumber}: ${errorMsg}\n`;
        });
    }

    if (response?.message) {
        successMessage += `\n${response.message}`;
    }

    return successMessage;
};

const AdminTripsPoliciesModifiedImport = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [importMode, setImportMode] = useState(IMPORT_MODES.STANDARD);
    const [specificPartner, setSpecificPartner] = useState('flower');
    const [importFile, setImportFile] = useState(null);
    const [importLoading, setImportLoading] = useState(false);
    const [importError, setImportError] = useState(null);
    const [importSuccess, setImportSuccess] = useState(() => {
        const savedMessage = localStorage.getItem('importModifiedPoliciesMessage');
        return savedMessage || null;
    });
    const [templateDownloading, setTemplateDownloading] = useState(false);

    const getActiveImportKey = () => (
        importMode === IMPORT_MODES.STANDARD ? 'standard' : specificPartner
    );

    const handleModeChange = (mode) => {
        if (!mode) return;
        setImportMode(mode);
        setImportFile(null);
        setImportError(null);
    };

    const handleSpecificPartnerChange = (partner) => {
        setSpecificPartner(partner);
        setImportFile(null);
        setImportError(null);
    };

    const handleImport = async () => {
        const importKey = getActiveImportKey();

        if (!importFile) {
            setImportError('Veuillez sélectionner un fichier Excel');
            return;
        }

        const importFn = IMPORT_FNS[importKey];
        if (!importFn) {
            setImportError('Type de partenaire non supporté pour les modifications');
            return;
        }

        setImportLoading(true);
        setImportError(null);
        setImportSuccess(null);

        try {
            const response = await importFn(importFile);
            const partnerName = PARTNER_LABELS[importKey] || importKey;
            const successMessage = buildTripModifiedSuccessMessage(response, partnerName);
            setImportSuccess(successMessage);
            localStorage.setItem('importModifiedPoliciesMessage', successMessage);
        } catch (err) {
            setImportError(handleApiError(err, 'Erreur lors de l\'import du fichier Excel'));
        } finally {
            setImportLoading(false);
        }
    };

    const handleReset = () => {
        setImportFile(null);
        setImportMode(IMPORT_MODES.STANDARD);
        setSpecificPartner('flower');
        setImportError(null);
        setImportSuccess(null);
        localStorage.removeItem('importModifiedPoliciesMessage');
    };

    const handleClearMessage = () => {
        setImportSuccess(null);
        localStorage.removeItem('importModifiedPoliciesMessage');
    };

    const handleDownloadTemplate = async () => {
        const key = getActiveImportKey();
        if (key === 'standard') {
            if (templateDownloading) return;
            setTemplateDownloading(true);
            setImportError(null);
            try {
                await downloadModifiedPoliciesStandardTemplate();
            } catch (err) {
                setImportError(handleApiError(
                    err,
                    t(`${PAGE_PREFIX}.standard.templateDownloadError`),
                ));
            } finally {
                setTemplateDownloading(false);
            }
            return;
        }
        if (key === 'odalys' || key === 'aloa') {
            alert(`Template indisponible pour ${PARTNER_LABELS[key]}.`);
            return;
        }
        const templateUrl = TEMPLATE_URLS[key];
        if (!templateUrl) {
            alert('Template indisponible pour ce format.');
            return;
        }
        window.open(templateUrl, '_blank', 'noopener,noreferrer');
    };

    const renderStandardColumnHelp = () => (
        <>
            <p className="text-muted small mb-2">{t(`${PAGE_PREFIX}.standard.endpointHint`)}</p>
            <p className="small mb-2">{t(`${PAGE_PREFIX}.standard.description`)}</p>
            <strong>{t(`${PAGE_PREFIX}.standard.columnsTitle`)}</strong>
            <ul className="mb-2 mt-2 small">
                <li><code>reservation_number</code></li>
                <li><code>new_trip_start_date</code></li>
                <li><code>new_trip_end_date</code></li>
                <li><code>new_trip_price</code></li>
            </ul>
            <p className="text-muted small mb-0">{t(`${PAGE_PREFIX}.standard.effectiveDatesNote`)}</p>
        </>
    );

    const renderPartnerColumnHelp = (partner) => {
        if (partner === 'flower') {
            return (
                <>
                    <strong>{t(`${PAGE_PREFIX}.flower.columnsTitle`)}</strong>
                    <ul className="mb-0 mt-2 small">
                        <li><code>reservation_number</code></li>
                        <li><code>new_trip_start_date</code></li>
                        <li><code>new_trip_end_date</code></li>
                        <li><code>new_trip_effective_start_date</code></li>
                        <li><code>new_trip_effective_end_date</code></li>
                        <li><code>new_distribution_type</code></li>
                        <li><code>{t(`${PAGE_PREFIX}.flower.extraColumn`)}</code></li>
                    </ul>
                </>
            );
        }
        if (partner === 'aloa') {
            return (
                <>
                    <strong>{t(`${PAGE_PREFIX}.aloa.columnsTitle`)}</strong>
                    <ul className="mb-0 mt-2 small">
                        <li><code>N° séjour</code></li>
                        <li><code>Arrivée le</code></li>
                        <li><code>Départ le</code></li>
                    </ul>
                </>
            );
        }
        if (partner === 'odalys') {
            return (
                <>
                    <p className="text-muted small mb-2">{t(`${PAGE_PREFIX}.odalys.endpointHint`)}</p>
                    <strong>{t(`${PAGE_PREFIX}.odalys.columnsTitle`)}</strong>
                    <ul className="mb-0 mt-2 small">
                        <li><code>reservation_number</code></li>
                        <li><code>activity_start_date</code></li>
                        <li><code>activity_end_date</code></li>
                        <li><code>uw_date</code></li>
                        <li><code>activity_price</code></li>
                        <li><code>first_name</code></li>
                        <li><code>last_name</code></li>
                        <li><code>email</code></li>
                        <li><code>activity_name</code></li>
                        <li><code>preferred_language</code></li>
                        <li><code>phone_number</code></li>
                        <li><code>activity_category</code></li>
                        <li><code>combined_addres</code></li>
                        <li><code>detail_modification</code></li>
                    </ul>
                </>
            );
        }
        if (partner === 'olela' || partner === 'lacdesgraves') {
            return (
                <>
                    <strong>{t(`${PAGE_PREFIX}.standard.columnsTitle`)}</strong>
                    <ul className="mb-0 mt-2 small">
                        <li><code>reservation_number</code></li>
                        <li><code>new_trip_start_date</code></li>
                        <li><code>new_trip_end_date</code></li>
                        <li><code>new_trip_effective_start_date</code></li>
                        <li><code>new_trip_effective_end_date</code></li>
                        <li><code>new_distribution_type</code></li>
                    </ul>
                </>
            );
        }
        return null;
    };

    const renderImportForm = () => {
        const importKey = getActiveImportKey();
        const fileHintKey = importKey === 'standard'
            ? `${PAGE_PREFIX}.standard.fileHint`
            : `${PAGE_PREFIX}.${importKey}.fileHint`;

        const fileAccept = importKey === 'odalys' ? '.xlsx,.xls,.csv' : '.xlsx,.xls';

        return (
            <Form>
                <Alert variant="light" className="border mb-4">
                    <div className="d-flex align-items-start gap-2">
                        <FiInfo className="mt-1 flex-shrink-0" />
                        <div className="flex-grow-1">
                            {importKey === 'standard'
                                ? renderStandardColumnHelp()
                                : renderPartnerColumnHelp(importKey)}
                        </div>
                    </div>
                </Alert>

                <Row>
                    <Col md={12}>
                        <Form.Group className="mb-4">
                            <Form.Label className="fw-bold">
                                Fichier Excel <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="file"
                                accept={fileAccept}
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                        setImportFile(file);
                                        setImportError(null);
                                    }
                                }}
                                disabled={importLoading}
                            />
                            <Form.Text className="text-muted">{t(fileHintKey)}</Form.Text>
                            {importFile && (
                                <div className="mt-2 small text-success">
                                    ✓ Fichier sélectionné : {importFile.name}
                                </div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <div className="d-flex justify-content-end gap-2 mt-4">
                    <Button variant="outline-secondary" onClick={handleReset} disabled={importLoading}>
                        {t('common.reset')}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleImport}
                        disabled={importLoading || !importFile}
                    >
                        {importLoading ? (
                            <>
                                <Spinner size="sm" className="me-2" animation="border" />
                                Import en cours...
                            </>
                        ) : (
                            <>
                                <FiUpload className="me-2" />
                                Importer
                            </>
                        )}
                    </Button>
                </div>
            </Form>
        );
    };

    return (
        <div className="dashboard-page admin-trips-policies-page">
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2 className="page-title">
                                <FiUpload className="me-2" />
                                Importer les modifications de séjours
                            </h2>
                            <p className="text-muted mb-0">
                                Importez un fichier Excel de séjours modifiés pour mettre à jour les dates de séjour et les polices/sinistres associés
                            </p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-primary"
                                onClick={handleDownloadTemplate}
                                disabled={templateDownloading || importLoading}
                                className="d-flex align-items-center"
                            >
                                {templateDownloading ? (
                                    <Spinner size="sm" className="me-2" animation="border" aria-hidden />
                                ) : (
                                    <FiDownload className="me-2" aria-hidden />
                                )}
                                Télécharger le template
                            </Button>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                    </div>

                    {importLoading && (
                        <Alert variant="info" className="mb-4">
                            <Spinner size="sm" className="me-2" animation="border" />
                            <strong>Import en cours...</strong> Cela peut prendre plusieurs minutes selon la taille du fichier. Veuillez patienter et ne pas fermer cette page.
                        </Alert>
                    )}

                    <Card className="import-form-card" style={{ border: '1px solid #e9ecef', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        <Card.Body style={{ padding: '2rem' }}>
                            <Tab.Container activeKey={importMode} onSelect={handleModeChange}>
                                <Nav variant="tabs" className="mb-4">
                                    <Nav.Item>
                                        <Nav.Link eventKey={IMPORT_MODES.STANDARD}>
                                            {t(`${PAGE_PREFIX}.pageTabs.standard`)}
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey={IMPORT_MODES.SPECIFIC}>
                                            {t(`${PAGE_PREFIX}.pageTabs.specific`)}
                                        </Nav.Link>
                                    </Nav.Item>
                                </Nav>

                                <Tab.Content>
                                    <Tab.Pane eventKey={IMPORT_MODES.STANDARD}>
                                        {renderImportForm()}
                                    </Tab.Pane>

                                    <Tab.Pane eventKey={IMPORT_MODES.SPECIFIC}>
                                        <Form.Group className="mb-4" style={{ maxWidth: '360px' }}>
                                            <Form.Label className="fw-bold">
                                                {t(`${PAGE_PREFIX}.specificPartnerLabel`)}
                                            </Form.Label>
                                            <Form.Select
                                                value={specificPartner}
                                                onChange={(e) => handleSpecificPartnerChange(e.target.value)}
                                                disabled={importLoading}
                                            >
                                                {SPECIFIC_PARTNER_OPTIONS.map((partner) => (
                                                    <option key={partner} value={partner}>
                                                        {t(`${PAGE_PREFIX}.specificPartners.${partner}`)}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                        {renderImportForm()}
                                    </Tab.Pane>
                                </Tab.Content>
                            </Tab.Container>
                        </Card.Body>
                    </Card>

                    {importError && (
                        <Alert variant="danger" className="mb-4 mt-4" dismissible onClose={() => setImportError(null)}>
                            <strong>Erreur :</strong> {importError}
                        </Alert>
                    )}

                    {importSuccess && (
                        <Alert
                            variant={importSuccess.includes('❌') || importSuccess.includes('Erreurs :') ? 'warning' : 'success'}
                            className="mb-4 mt-4"
                            dismissible
                            onClose={handleClearMessage}
                            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                            <strong>Résultat de l&apos;import :</strong>
                            <pre style={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                marginTop: '10px',
                                marginBottom: 0,
                                fontSize: '0.9rem',
                                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                padding: '10px',
                                borderRadius: '4px',
                                fontFamily: 'inherit',
                                maxHeight: '400px',
                                overflowY: 'auto',
                            }}
                            >
                                {importSuccess}
                            </pre>
                        </Alert>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminTripsPoliciesModifiedImport;
